import useAlgoliaTracking from './algolia-tracking'
import AlgoliaInsight from './algolia-insight'
import algoliaConfig from './algolia-config'

const { trackingSendClick, trackingSendClickObjectIDs, trackingSendConvertedAddToCart, trackingSendView, trackingSendAddedToCart } =
  useAlgoliaTracking()

const CLICK_STORAGE_KEY = 'algolia_analytics_clicked_objects'

const initTrackingClient = () => {
  const { algoliaConfig } = window.GM_STATE.integrations

  const AlgoliaInsightInstance = new AlgoliaInsight({
    siteId: algoliaConfig.siteId,
    apiKey: algoliaConfig.apiKey
  })

  return AlgoliaInsightInstance
}

const storeClickForPixel = (
  objectId: string | number,
  position: number | null,
  queryId?: string,
  productId?: string | number,
  allowNoQuery = false
) => {
  if (!queryId && !allowNoQuery) {
    return
  }

  const record: {
    index: string
    objectIDs: string[]
    positions: number[]
    queryID?: string
    productId?: string
  } = {
    index: algoliaConfig.indexName,
    objectIDs: [`${objectId}`],
    positions: position !== null ? [position] : [],
    queryID: queryId
  }

  if (productId) {
    record.productId = `${productId}`
  }

  try {
    const stored = localStorage.getItem(CLICK_STORAGE_KEY)
    const items = stored ? JSON.parse(stored) : []

    const exists = items.some((item: any) => {
      const sameObject = Array.isArray(item.objectIDs) && item.objectIDs[0] === record.objectIDs[0]
      const sameQuery = (item.queryID || '') === (record.queryID || '')
      const sameProduct = (item.productId || '') === (record.productId || '')

      return sameObject && sameQuery && sameProduct
    })

    if (!exists) {
      items.push(record)
    }

    localStorage.setItem(CLICK_STORAGE_KEY, JSON.stringify(items))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to store Algolia click for pixel attribution:', error)
  }
}

const handleTracking = (
  instanceAlgoliaTracking: any,
  url: string,
  objectId: string | number,
  index: number,
  queryId: string,
  productId?: string | number
) => {
  trackingSendClick(instanceAlgoliaTracking, { objectIDs: [`${objectId}`], positions: [index + 1], queryID: queryId })
  storeClickForPixel(objectId, index + 1, queryId, productId)

  window.location.href = url
}

const handleTrackingObjectIDs = (instanceAlgoliaTracking: any, url: string, objectId: string | number, ...rest: any[]) => {
  // Some callers pass (instance, url, objectId), others pass extra args like:
  // - (index, queryID, productId) from PlpAlgoliaCard
  // - (index, queryID) from instant search
  // - (productId) from any future direct calls
  const productIdArg = rest.length >= 3 ? rest[rest.length - 1] : (rest.length === 1 ? rest[0] : undefined)
  const productIdCandidate = typeof productIdArg === 'number' ? productIdArg : Number(`${productIdArg}`)
  const productId = Number.isFinite(productIdCandidate) && productIdCandidate > 0 ? productIdCandidate : undefined

  trackingSendClickObjectIDs(instanceAlgoliaTracking, { objectIDs: [`${objectId}`] })
  storeClickForPixel(objectId, null, undefined, productId, true)

  window.location.href = url
}

const handleTrackingConvertedAddToCart = (instanceAlgoliaTracking: any, objectId: string | number) => {
  trackingSendConvertedAddToCart(instanceAlgoliaTracking, { objectIDs: [`${objectId}`] })
}

const handleTrackingSendView = (instanceAlgoliaTracking: any, objectId: string | number | string[]) => {
  const objectIDs = Array.isArray(objectId) ? objectId.map((id) => `${id}`) : [`${objectId}`]

  trackingSendView(instanceAlgoliaTracking, { objectIDs })
}

const handleTrackingViewObjectIDs = (instanceAlgoliaTracking: any, objectIds: string[], queryId?: string) => {
  trackingSendView(instanceAlgoliaTracking, { objectIDs: objectIds, queryID: queryId })
}

const handleTrackingAddedToCart = (
  instanceAlgoliaTracking: any,
  objectId: string[],
  objectItems: string[],
  currencyName: string
) => {
  trackingSendAddedToCart(instanceAlgoliaTracking, { objectIDs: objectId, objectData: objectItems, currency: currencyName })
}

export {
  initTrackingClient,
  handleTrackingObjectIDs,
  handleTracking,
  handleTrackingConvertedAddToCart,
  handleTrackingSendView,
  handleTrackingViewObjectIDs,
  handleTrackingAddedToCart
}
